import { useState, useEffect } from "react";
import { DollarSign, Plus, RefreshCw, Calendar, FileText, CheckCircle, Play, ClipboardCheck, CreditCard, History, Calculator, BadgeDollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";

const STATUS_BADGE = {
  DRAFT: "bg-gray-100 text-gray-700",
  PROCESSING: "bg-blue-100 text-blue-700",
  REVIEW: "bg-amber-100 text-amber-700",
  APPROVED: "bg-green-100 text-green-700",
  PAID: "bg-emerald-100 text-emerald-700",
};

export default function SmartPayPayroll() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");

  const [dashboard, setDashboard] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [runs, setRuns] = useState([]);
  const [runDetails, setRunDetails] = useState([]);
  const [taxConfigs, setTaxConfigs] = useState([]);
  const [auditLog, setAuditLog] = useState([]);

  const [showNewCalendar, setShowNewCalendar] = useState(false);
  const [showNewPeriod, setShowNewPeriod] = useState(false);
  const [showNewRun, setShowNewRun] = useState(false);
  const [showNewTax, setShowNewTax] = useState(false);

  const [newCalendar, setNewCalendar] = useState({ calendar_name: "", frequency: "MONTHLY", country: "", start_date: "", end_date: "" });
  const [newPeriod, setNewPeriod] = useState({ calendar_id: "", period_number: "", start_date: "", end_date: "", payment_date: "" });
  const [newRun, setNewRun] = useState({ period_id: "", run_type: "REGULAR", name: "", currency: "USD", processed_by: "" });
  const [newTax, setNewTax] = useState({ country: "", tax_type: "INCOME_TAX", description: "", brackets: "", standard_deduction: 0, effective_year: 2026 });

  const fetchDashboard = async () => { try { const res = await axios.get("/api/smartpay/payroll/dashboard"); setDashboard(res.data); } catch {} };
  const fetchCalendars = async () => { try { const res = await axios.get("/api/smartpay/payroll/calendars"); setCalendars(res.data.calendars || []); } catch {} };
  const fetchPeriods = async () => { try { const res = await axios.get("/api/smartpay/payroll/periods"); setPeriods(res.data.periods || []); } catch {} };
  const fetchRuns = async () => { try { const res = await axios.get("/api/smartpay/payroll/runs"); setRuns(res.data.runs || []); } catch {} };
  const fetchTaxConfigs = async () => { try { const res = await axios.get("/api/smartpay/payroll/tax-configurations"); setTaxConfigs(res.data.tax_configs || []); } catch {} };
  const fetchAuditLog = async () => { try { const res = await axios.get("/api/smartpay/payroll/audit"); setAuditLog(res.data.audit_log || []); } catch {} };

  useEffect(() => { fetchDashboard(); fetchCalendars(); fetchPeriods(); fetchRuns(); fetchTaxConfigs(); fetchAuditLog(); }, []);

  const handleCreateCalendar = async () => {
    try { await axios.post("/api/smartpay/payroll/calendars", newCalendar); toast({ title: "Calendar created" }); setShowNewCalendar(false); fetchCalendars(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };
  const handleCreatePeriod = async () => {
    try { await axios.post("/api/smartpay/payroll/periods", newPeriod); toast({ title: "Period created" }); setShowNewPeriod(false); fetchPeriods(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };
  const handleCreateRun = async () => {
    try { await axios.post("/api/smartpay/payroll/runs", newRun); toast({ title: "Run created" }); setShowNewRun(false); fetchRuns(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };
  const handleCreateTax = async () => {
    try {
      const brackets = newTax.brackets ? newTax.brackets.split("\n").filter(Boolean).map(line => {
        const parts = line.split(",");
        return { min_annual: parseFloat(parts[0]) || 0, max_annual: parseFloat(parts[1]) || 0, rate: parseFloat(parts[2]) || 0 };
      }) : [];
      await axios.post("/api/smartpay/payroll/tax-configurations", { ...newTax, brackets });
      toast({ title: "Tax config created" }); setShowNewTax(false); fetchTaxConfigs();
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleRunAction = async (runId, action) => {
    try {
      await axios.post(`/api/smartpay/payroll/runs/${runId}/${action}`, { user_id: "admin" });
      toast({ title: `Run ${action}d` }); fetchRuns();
    } catch (e) { toast({ title: e.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  const handleCalculateRun = async (runId) => {
    try {
      const res = await axios.post(`/api/smartpay/payroll/runs/${runId}/calculate`);
      toast({ title: `Calculated ${res.data.success || 0} employees` }); fetchRuns();
    } catch (e) { toast({ title: e.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  const handleFetchDetails = async (runId) => {
    try { const res = await axios.get(`/api/smartpay/payroll/runs/${runId}/details`); setRunDetails(res.data.details || []); setActiveTab("details"); } catch {}
  };

  const kpiCards = [
    { title: "Active Runs", value: dashboard?.active_runs || 0, icon: FileText, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
    { title: "Approved / Paid", value: dashboard?.approved_runs || 0, icon: CheckCircle, color: "text-green-600", bg: "bg-green-50", border: "border-green-200" },
    { title: "Total Gross", value: dashboard?.total_gross ? `$${dashboard.total_gross.toLocaleString()}` : "$0", icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
    { title: "Draft Runs", value: dashboard?.draft_runs || 0, icon: FileText, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payroll Processing</h1>
          <p className="text-muted-foreground">Multi-country payroll runs & tax calculations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchRuns(); }}><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          <Dialog open={showNewRun} onOpenChange={setShowNewRun}>
            <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Run</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Payroll Run</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={newRun.name} onChange={e => setNewRun({...newRun, name: e.target.value})} /></div>
                <div><Label>Period ID</Label><Input value={newRun.period_id} onChange={e => setNewRun({...newRun, period_id: e.target.value})} /></div>
                <div><Label>Currency</Label>
                  <Select value={newRun.currency} onValueChange={v => setNewRun({...newRun, currency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="ZAR">ZAR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreateRun}>Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map(kpi => (
          <Card key={kpi.title} className={`${kpi.bg} ${kpi.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.title}</p>
                  <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                </div>
                <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-70`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="runs">Payroll Runs</TabsTrigger>
          <TabsTrigger value="periods">Periods</TabsTrigger>
          <TabsTrigger value="tax">Tax Config</TabsTrigger>
          <TabsTrigger value="details">Run Details</TabsTrigger>
          <TabsTrigger value="audit">History</TabsTrigger>
        </TabsList>

        <TabsContent value="runs" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle>Payroll Runs</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run #</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Gross</TableHead>
                    <TableHead>Net</TableHead>
                    <TableHead>Employees</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map(run => (
                    <TableRow key={run.id}>
                      <TableCell className="font-mono text-xs">{run.run_number}</TableCell>
                      <TableCell>{run.name}</TableCell>
                      <TableCell><Badge className={STATUS_BADGE[run.status]}>{run.status}</Badge></TableCell>
                      <TableCell>${(run.total_gross || 0).toLocaleString()}</TableCell>
                      <TableCell>${(run.total_net || 0).toLocaleString()}</TableCell>
                      <TableCell>{run.total_employees || 0}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {run.status === "DRAFT" && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handleRunAction(run.id, "process")}><Play className="h-3 w-3" /></Button>
                              <Button size="sm" variant="outline" onClick={() => handleCalculateRun(run.id)}><Calculator className="h-3 w-3" /></Button>
                            </>
                          )}
                          {run.status === "PROCESSING" && <Button size="sm" variant="outline" onClick={() => handleRunAction(run.id, "review")}><ClipboardCheck className="h-3 w-3" /></Button>}
                          {run.status === "REVIEW" && <Button size="sm" variant="outline" onClick={() => handleRunAction(run.id, "approve")}><CheckCircle className="h-3 w-3" /></Button>}
                          {run.status === "APPROVED" && <Button size="sm" variant="outline" onClick={() => handleRunAction(run.id, "pay")}><CreditCard className="h-3 w-3" /></Button>}
                          <Button size="sm" variant="ghost" onClick={() => handleFetchDetails(run.id)}><BadgeDollarSign className="h-3 w-3" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {runs.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No payroll runs yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="periods" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Payroll Periods</CardTitle>
              <Dialog open={showNewPeriod} onOpenChange={setShowNewPeriod}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Period</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Period</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Calendar ID</Label><Input value={newPeriod.calendar_id} onChange={e => setNewPeriod({...newPeriod, calendar_id: e.target.value})} /></div>
                    <div><Label>Period #</Label><Input value={newPeriod.period_number} onChange={e => setNewPeriod({...newPeriod, period_number: e.target.value})} /></div>
                    <div><Label>Start Date</Label><Input type="date" value={newPeriod.start_date} onChange={e => setNewPeriod({...newPeriod, start_date: e.target.value})} /></div>
                    <div><Label>End Date</Label><Input type="date" value={newPeriod.end_date} onChange={e => setNewPeriod({...newPeriod, end_date: e.target.value})} /></div>
                    <div><Label>Payment Date</Label><Input type="date" value={newPeriod.payment_date} onChange={e => setNewPeriod({...newPeriod, payment_date: e.target.value})} /></div>
                    <Button onClick={handleCreatePeriod}>Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period #</TableHead>
                    <TableHead>Calendar</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {periods.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.period_number}</TableCell>
                      <TableCell className="font-mono text-xs">{p.calendar_id?.slice(-8)}</TableCell>
                      <TableCell>{p.start_date}</TableCell>
                      <TableCell>{p.end_date}</TableCell>
                      <TableCell>{p.payment_date}</TableCell>
                      <TableCell><Badge variant="outline">{p.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tax" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Tax Configurations</CardTitle>
              <Dialog open={showNewTax} onOpenChange={setShowNewTax}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Tax Config</Button></DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Create Tax Configuration</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Country</Label>
                      <Select value={newTax.country} onValueChange={v => setNewTax({...newTax, country: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US">United States</SelectItem>
                          <SelectItem value="UK">United Kingdom</SelectItem>
                          <SelectItem value="CA">Canada</SelectItem>
                          <SelectItem value="AU">Australia</SelectItem>
                          <SelectItem value="DE">Germany</SelectItem>
                          <SelectItem value="FR">France</SelectItem>
                          <SelectItem value="IN">India</SelectItem>
                          <SelectItem value="AE">UAE</SelectItem>
                          <SelectItem value="SG">Singapore</SelectItem>
                          <SelectItem value="ZA">South Africa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Description</Label><Input value={newTax.description} onChange={e => setNewTax({...newTax, description: e.target.value})} /></div>
                    <div><Label>Standard Deduction</Label><Input type="number" value={newTax.standard_deduction} onChange={e => setNewTax({...newTax, standard_deduction: parseFloat(e.target.value) || 0})} /></div>
                    <div><Label>Brackets (min,max,rate per line)</Label><textarea className="w-full min-h-[80px] border rounded p-2 text-sm" value={newTax.brackets} onChange={e => setNewTax({...newTax, brackets: e.target.value})} placeholder="0,11925,0.10&#10;11925,48475,0.12" /></div>
                    <Button onClick={handleCreateTax}>Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Standard Deduction</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Brackets</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxConfigs.map(t => (
                    <TableRow key={t.id}>
                      <TableCell><Badge variant="outline">{t.country}</Badge></TableCell>
                      <TableCell>{t.description}</TableCell>
                      <TableCell>${(t.standard_deduction || 0).toLocaleString()}</TableCell>
                      <TableCell>{t.effective_year}</TableCell>
                      <TableCell className="font-mono text-xs">{t.brackets?.length || 0} brackets</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle>Run Details</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Gross</TableHead>
                    <TableHead>Income Tax</TableHead>
                    <TableHead>SS/Med</TableHead>
                    <TableHead>Net Pay</TableHead>
                    <TableHead>Employer Cost</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runDetails.map(d => (
                    <TableRow key={d.id}>
                      <TableCell className="font-mono text-xs">{d.employee_id?.slice(-8)}</TableCell>
                      <TableCell>${(d.gross_pay || 0).toLocaleString()}</TableCell>
                      <TableCell>${(d.income_tax || 0).toLocaleString()}</TableCell>
                      <TableCell>${(d.social_security_tax || 0).toLocaleString()}</TableCell>
                      <TableCell className="font-bold">${(d.net_pay || 0).toLocaleString()}</TableCell>
                      <TableCell>${(d.total_employer_cost || 0).toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline">{d.payment_status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {runDetails.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Select a run to view details</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle>Audit Trail</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLog.map(a => (
                    <TableRow key={a.id}>
                      <TableCell className="text-xs">{a.created_at?.slice(0, 10)}</TableCell>
                      <TableCell><Badge variant="secondary">{a.action}</Badge></TableCell>
                      <TableCell>{a.from_status}</TableCell>
                      <TableCell>{a.to_status}</TableCell>
                      <TableCell className="text-sm">{a.notes}</TableCell>
                    </TableRow>
                  ))}
                  {auditLog.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No audit entries yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
